/* ============================================
BOOST CUSTOMISATION START
PDP Description: extract Snapshot / Fitment Guide anchors

The product description (in the Body HTML field) contains a centred <p>
holding two <a><img></a> links — one with alt="Product Snapshot" and one
with alt="Sizing Chart". The accompanying liquid snippet
(boost-product-description-rolled.liquid) leaves those anchors inside the
Description accordion. This script:

  1. Finds the Snapshot / Chart anchors inside the Description content and
     caches their hrefs.
  2. Removes them from the description (and removes the now-empty wrapper
     <p> if applicable).
  3. Renders the Product Snapshot link into the .boost-pdp-doc-row
     container above the accordions (Snapshot only; revealed only when a
     Snapshot exists).
  4. Renders the Fitment Guide into the [data-boost-fitment-guide] slot on
     the Size heading row (main-product-info.liquid). ALWAYS shown:
       - Chart present  -> clickable link, chart icon + "Fitment Guide".
       - No chart, Snapshot present -> note "See Product Snapshot
         for fitment details." with "Product Snapshot" linking to the
         Snapshot PDF (no icon).
       - No chart, no Snapshot -> same note, "Product Snapshot" as plain
         text (nothing to link to).

  Re-render handling (2026-06-12): the theme's variant JS (custom-async.js)
  REPLACES the entire .f8pr-variant-selection markup on a colour or size
  change (server-rendered). That wipes the Fitment Guide slot, which lives
  inside that container. We mirror boost-colour-hover.js: a MutationObserver
  on the parent of .f8pr-variant-selection re-populates the slot from the
  cached hrefs after each replacement. (The Snapshot row and the description
  accordion are outside .f8pr-variant-selection and are not replaced, so
  they are populated once.)

NOTE on class naming: classes deliberately avoid the substring "icon"
because Boost's screen.css contains rules using [class*="icon"] which
override font-family to the icon-glyph font ("i"), breaking text
rendering on any element whose class contains "icon" as a substring.
We use "doc" / "fitment" instead.

BOOST UPDATE 2026-06-21: wording changed from "Refer to the" to "See", and
link styling is injected here (Boost link convention: red, no underline at
rest, underline on hover) for the fitment note link and the PDP Share link,
so the theme's default styling cannot override them. For the Share link the
icon and word are red at rest and only the word "Share" underlines on hover.

Catalogue audit (May 2026, 2,997 unique products):
  - 96% have a Snapshot (Snapshot row shown)
  - 37% have a Chart (Fitment Guide rendered as a link)
  - 60% have Snapshot but no Chart (note, "Product Snapshot" linked)
  - 3% have neither (Snapshot row hidden; note shown, "Product Snapshot"
    plain text — accepted, per the confirmed always-display behaviour)
============================================ */
(function () {
  'use strict';

  // Brand icon image URLs.
  // BOOST CUSTOMISATION 2026-08-23: icons repointed to the new brand icon
  // files (images-product-snapshot.png and images-fitment-guide.png),
  // replacing images-snapshot.png and images-sizechart.png on the PDP only.
  var SNAPSHOT_ICON_URL = 'https://cdn.shopify.com/s/files/1/0994/7319/5307/files/images-product-snapshot.png?v=1787483697';
  var CHART_ICON_URL = 'https://cdn.shopify.com/s/files/1/0994/7319/5307/files/images-fitment-guide.png?v=1787483679';

  // Fallback note parts. "Product Snapshot" becomes a link when a Snapshot
  // exists, otherwise it stays as plain text.
  var FITMENT_NOTE_PRE = 'See ';
  var FITMENT_NOTE_LINKTEXT = 'Product Snapshot';
  var FITMENT_NOTE_POST = ' for fitment details.';

  // Cached anchor state, resolved once from the (stable) description.
  var cache = {
    resolved: false,
    chartExists: false,
    chartHref: null,
    snapshotExists: false,
    snapshotHref: null
  };

  // BOOST UPDATE 2026-06-21: guarantee the fitment note link and the PDP Share
  // link follow the Boost link convention (Boost Red at rest, underline on
  // hover). For the Share link the icon and word are red at rest and only the
  // word "Share" underlines on hover (not the icon, not the gap). Injected once
  // so the theme's default styling cannot override it.
  function injectStyles() {
    if (document.getElementById('boost-pdp-inject-style')) return;
    var style = document.createElement('style');
    style.id = 'boost-pdp-inject-style';
    style.textContent =
      '.boost-fitment-note-link{color:#ee3d24 !important;text-decoration:none !important;}' +
      '.boost-fitment-note-link:hover,.boost-fitment-note-link:focus-visible{text-decoration:underline !important;}' +
      'a.toggle[aria-label="Share"],a.toggle[aria-label="Share"]:hover,a.toggle[aria-label="Share"]:focus,a.toggle[aria-label="Share"]:focus-visible{color:#ee3d24 !important;text-decoration:none !important;}' +
      'a.toggle[aria-label="Share"] .icon-share{color:#ee3d24 !important;}' +
      'a.toggle[aria-label="Share"]:hover .mobile-hide,a.toggle[aria-label="Share"]:focus-visible .mobile-hide{text-decoration:underline !important;}';
    (document.head || document.documentElement).appendChild(style);
  }

  function isVisible(el) {
    // True if the element and all ancestors are not display:none. Lets us
    // skip the desktop-only container on mobile (and vice versa).
    while (el && el !== document.body) {
      if (window.getComputedStyle(el).display === 'none') return false;
      el = el.parentElement;
    }
    return true;
  }

  function escapeAttr(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function removeAnchor(a) {
    if (!a) return;
    var parent = a.parentNode;
    if (!parent) return;
    parent.removeChild(a);
    // Remove the wrapper <p> if it is now empty.
    if (parent.tagName && parent.tagName.toLowerCase() === 'p') {
      if (!parent.textContent.trim() && !parent.querySelector('img')) {
        if (parent.parentNode) parent.parentNode.removeChild(parent);
      }
    }
  }

  // Resolve the Snapshot / Chart anchors from the visible description,
  // cache their hrefs, populate the Snapshot row, and strip the anchors.
  // Runs once (the description is not re-rendered on variant change).
  function resolveAndCache() {
    if (cache.resolved) return;

    var descContents = document.querySelectorAll('[data-boost-pdp-description-content]');
    var descContent = null;
    for (var j = 0; j < descContents.length; j++) {
      if (isVisible(descContents[j])) { descContent = descContents[j]; break; }
    }

    var snapshotAnchor = null;
    var chartAnchor = null;
    if (descContent) {
      var anchors = descContent.querySelectorAll('a');
      for (var k = 0; k < anchors.length; k++) {
        var img = anchors[k].querySelector('img');
        if (!img) continue;
        var alt = (img.getAttribute('alt') || '').trim().toLowerCase();
        if (!snapshotAnchor && alt === 'product snapshot') {
          snapshotAnchor = anchors[k];
        } else if (!chartAnchor && (alt === 'sizing chart' || alt === 'size chart')) {
          chartAnchor = anchors[k];
        }
      }
    }

    if (snapshotAnchor) {
      cache.snapshotExists = true;
      cache.snapshotHref = snapshotAnchor.getAttribute('href') || '#';
    }
    if (chartAnchor) {
      cache.chartExists = true;
      cache.chartHref = chartAnchor.getAttribute('href') || '#';
    }
    cache.resolved = true;

    populateSnapshotRow();

    // Strip the originals so they don't also appear inside the Description.
    removeAnchor(snapshotAnchor);
    removeAnchor(chartAnchor);
  }

  // Snapshot row above the accordions. Populated once; not affected by
  // variant re-renders.
  function populateSnapshotRow() {
    if (!cache.snapshotExists) return;
    var rows = document.querySelectorAll('[data-boost-pdp-doc-row]');
    var row = null;
    for (var i = 0; i < rows.length; i++) {
      if (isVisible(rows[i])) { row = rows[i]; break; }
    }
    if (!row || row.getAttribute('data-boost-snapshot-done') === '1') return;
    row.innerHTML =
      '<a class="boost-pdp-doc-link boost-pdp-doc-snapshot" ' +
        'href="' + escapeAttr(cache.snapshotHref) + '" ' +
        'target="_blank" rel="noopener" ' +
        'aria-label="Open Product Snapshot (PDF)">' +
        '<img class="boost-pdp-doc-img" ' +
          'src="' + escapeAttr(SNAPSHOT_ICON_URL) + '" ' +
          'alt="" loading="lazy">' +
        '<span>Product Snapshot</span>' +
      '</a>';
    row.removeAttribute('hidden');
    row.setAttribute('data-boost-snapshot-done', '1');
  }

  // Build the Fitment Guide inner HTML from the cache.
  function fitmentHTML() {
    if (cache.chartExists) {
      return '<a class="boost-fitment-link" ' +
          'href="' + escapeAttr(cache.chartHref) + '" ' +
          'target="_blank" rel="noopener" ' +
          'aria-label="Open Fitment Guide (PDF)">' +
          '<img class="boost-fitment-img" ' +
            'src="' + escapeAttr(CHART_ICON_URL) + '" ' +
            'alt="" loading="lazy">' +
          '<span>Fitment Guide</span>' +
        '</a>';
    }
    if (cache.snapshotExists) {
      return '<span class="boost-fitment-note" role="note">' +
          FITMENT_NOTE_PRE +
          '<a class="boost-fitment-note-link" ' +
            'href="' + escapeAttr(cache.snapshotHref) + '" ' +
            'target="_blank" rel="noopener">' +
            FITMENT_NOTE_LINKTEXT +
          '</a>' +
          FITMENT_NOTE_POST +
        '</span>';
    }
    return '<span class="boost-fitment-note" role="note">' +
        FITMENT_NOTE_PRE + FITMENT_NOTE_LINKTEXT + FITMENT_NOTE_POST +
      '</span>';
  }

  // Fill every visible, currently-empty Fitment Guide slot. Filling only
  // empty slots makes this safe to call repeatedly (and avoids a feedback
  // loop with the MutationObserver, since our own write leaves the slot
  // non-empty).
  function populateFitment() {
    var slots = document.querySelectorAll('[data-boost-fitment-guide]');
    if (!slots.length) return;
    var html = fitmentHTML();
    for (var i = 0; i < slots.length; i++) {
      var slot = slots[i];
      if (!isVisible(slot)) continue;
      if (slot.innerHTML.trim() !== '') continue;
      slot.innerHTML = html;
    }
  }

  // The theme replaces .f8pr-variant-selection (server-rendered) on a colour
  // or size change, wiping the Fitment Guide slot inside it. Watch the parent
  // for the child replacement and re-populate, deferring one tick so the
  // theme's own post-replacement work runs first. Mirrors the pattern in
  // boost-colour-hover.js.
  function initVariantObserver() {
    var variantSelection = document.querySelector('.f8pr-variant-selection');
    if (!variantSelection || !variantSelection.parentNode) return;
    var observer = new MutationObserver(function () {
      setTimeout(populateFitment, 0);
    });
    observer.observe(variantSelection.parentNode, { childList: true });
  }

  function init() {
    injectStyles();
    resolveAndCache();
    populateFitment();
    initVariantObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================
BOOST CUSTOMISATION END
============================================ */
